import axios from 'axios'
import {
  superAdminListInstructionContent,
  superAdminCreateInstructionContent,
  superAdminUpdateInstructionContent,
  superAdminAddInstructionContentVersion,
  superAdminDeleteInstructionContent,
  superAdminInstructionContentUploadUrl,
  superAdminInstructionContentUrl,
  InstructionContent,
  InstructionContentPayload,
} from '@training24/course/src/instruction'

interface InstructionLibraryState {
  items: InstructionContent[]
  loaded: boolean
}

export type { InstructionContent }

// Dateiname für den Markdown-Quelltext eines Editor-Dokuments (der Key im
// Bucket wird serverseitig mit UUID-Präfix vergeben, der Name ist kosmetisch).
const MARKDOWN_FILE_NAME = 'dokument.md'

export default {
  namespaced: true,

  state: (): InstructionLibraryState => ({
    items: [],
    loaded: false,
  }),

  getters: {
    isLoaded: (state: InstructionLibraryState): boolean => state.loaded,
    getItems: (state: InstructionLibraryState): InstructionContent[] => state.items,
    // contentId → Titel, z. B. um Sektionen bestehender Unterweisungen zu benennen.
    getContentTitle: (state: InstructionLibraryState) => (contentId: string): string => {
      const c = state.items.find((x) => x.contentId === contentId)
      return c ? c.title : ''
    },
  },

  actions: {
    async LOAD(context: any, opts?: { force?: boolean }) {
      if (context.state.loaded && !(opts && opts.force)) return
      const jwt = context.rootGetters['getJWT']
      const items = await superAdminListInstructionContent(jwt)
      context.commit('SET', items)
    },

    // Pre-signed Anzeige-URL zur aktuellen Version eines MEDA-Dokuments (Vorschau).
    async GET_DOC_URL(context: any, { contentId }: { contentId: string }) {
      const jwt = context.rootGetters['getJWT']
      return superAdminInstructionContentUrl(contentId, jwt)
    },

    // Markdown-Quelltext der aktuellen Version laden (zum Bearbeiten im Editor).
    async GET_DOC_TEXT(context: any, { contentId }: { contentId: string }): Promise<string> {
      const res = await context.dispatch('GET_DOC_URL', { contentId })
      if (!res || !res.url) throw new Error('Keine Dokument-URL erhalten')
      const { data } = await axios.get(res.url, { responseType: 'text', transformResponse: [(d) => d] })
      return String(data)
    },

    async CREATE(context: any, payload: InstructionContentPayload) {
      const jwt = context.rootGetters['getJWT']
      const item = await superAdminCreateInstructionContent(payload, jwt)
      context.commit('UPSERT', item)
      return item
    },

    async UPDATE(
      context: any,
      { contentId, payload }: { contentId: string; payload: Partial<InstructionContentPayload> }
    ) {
      const jwt = context.rootGetters['getJWT']
      const item = await superAdminUpdateInstructionContent(contentId, payload, jwt)
      context.commit('UPSERT', item)
      return item
    },

    async ADD_VERSION(context: any, { contentId, payload }: { contentId: string; payload: any }) {
      const jwt = context.rootGetters['getJWT']
      const item = await superAdminAddInstructionContentVersion(contentId, payload, jwt)
      context.commit('UPSERT', item)
      return item
    },

    async DELETE(context: any, contentId: string) {
      const jwt = context.rootGetters['getJWT']
      await superAdminDeleteInstructionContent(contentId, jwt)
      context.commit('REMOVE', contentId)
    },

    // Datei in den MEDA-Bibliotheks-Bucket laden und die Versions-Felder zurückgeben.
    async UPLOAD_FILE(context: any, { file }: { file: File }) {
      const jwt = context.rootGetters['getJWT']
      const contentType = file.type
      const fileType: 'pdf' | 'docx' = contentType === 'application/pdf' ? 'pdf' : 'docx'
      const { uploadUrl, key } = await superAdminInstructionContentUploadUrl(file.name, contentType, jwt)
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': contentType } })
      return { fileName: file.name, fileKey: key, fileType }
    },

    // Markdown-Quelltext als unveränderliche Version in den Bucket laden.
    async UPLOAD_MARKDOWN(context: any, { text }: { text: string }) {
      const jwt = context.rootGetters['getJWT']
      const { uploadUrl, key } = await superAdminInstructionContentUploadUrl(
        MARKDOWN_FILE_NAME,
        'text/markdown',
        jwt
      )
      await axios.put(uploadUrl, text, { headers: { 'Content-Type': 'text/markdown' } })
      return { fileName: MARKDOWN_FILE_NAME, fileKey: key, fileType: 'md' }
    },

    // Komfort: Baustein anlegen + Datei hochladen + erste Version in einem Schritt.
    async CREATE_WITH_FILE(
      context: any,
      { title, file, confirmationItems, published }:
        { title: string; file: File; confirmationItems?: string[]; published?: boolean }
    ): Promise<InstructionContent> {
      const fileType: 'pdf' | 'docx' = file.type === 'application/pdf' ? 'pdf' : 'docx'
      const content = await context.dispatch('CREATE', {
        title,
        fileType,
        confirmationItems: confirmationItems || [],
        published: !!published,
      })
      const versionPayload = await context.dispatch('UPLOAD_FILE', { file })
      return context.dispatch('ADD_VERSION', { contentId: content.contentId, payload: versionPayload })
    },

    // Komfort: Editor-Dokument anlegen + Markdown als erste Version speichern.
    async CREATE_WITH_MARKDOWN(
      context: any,
      { title, text, confirmationItems, published }:
        { title: string; text: string; confirmationItems?: string[]; published?: boolean }
    ): Promise<InstructionContent> {
      const content = await context.dispatch('CREATE', {
        title,
        fileType: 'md' as any,
        confirmationItems: confirmationItems || [],
        published: !!published,
      })
      const versionPayload = await context.dispatch('UPLOAD_MARKDOWN', { text })
      return context.dispatch('ADD_VERSION', { contentId: content.contentId, payload: versionPayload })
    },

    async ADD_VERSION_FILE(context: any, { contentId, file }: { contentId: string; file: File }) {
      const versionPayload = await context.dispatch('UPLOAD_FILE', { file })
      return context.dispatch('ADD_VERSION', { contentId, payload: versionPayload })
    },

    async ADD_VERSION_MARKDOWN(context: any, { contentId, text }: { contentId: string; text: string }) {
      const versionPayload = await context.dispatch('UPLOAD_MARKDOWN', { text })
      return context.dispatch('ADD_VERSION', { contentId, payload: versionPayload })
    },
  },

  mutations: {
    SET(state: InstructionLibraryState, items: InstructionContent[]) {
      state.items = items
      state.loaded = true
    },
    UPSERT(state: InstructionLibraryState, item: InstructionContent) {
      const others = state.items.filter((c) => c.contentId !== item.contentId)
      state.items = [...others, item]
    },
    REMOVE(state: InstructionLibraryState, contentId: string) {
      state.items = state.items.filter((c) => c.contentId !== contentId)
    },
  },
}
